/**
 * The one place the app builds a backend URL (ADR-038, CLA-1417).
 *
 * These are deliberately thin — `apiUrl` returns its argument unchanged. The
 * value is not the transformation, it is having a single named seam that
 * `api-url.guard.test.ts` can enforce: a bare `fetch('/api/v1/…')` anywhere in
 * `web/src` fails the build.
 *
 * That is worth a no-op function because the surface had already scattered to
 * 44 hand-built literals across the admin screens, the auth pages, the portal
 * client, incident export and both WebSocket call sites — and the identical
 * drift on the Elixir side shipped a live defect (CLA-1420: three link builders
 * bypassing `ClaroWeb.PublicURL`, so US-realm links lost realm pinning and
 * routed to the AU deployment).
 *
 * ⚠ **`apiUrl` is a no-op by design, not by omission.** The SPA is served from
 * the same host as its API — `au.claro.clinic` serves both under ADR-038, as
 * `claro.clinic` does today — so there is no origin to prepend. An earlier
 * draft of ADR-038 had the SPA calling a *different* origin per realm, and this
 * module carried `VITE_API_ORIGIN_AU`/`_US` to switch it. That approach was
 * abandoned: host-only session cookies cannot span origins, and the chosen
 * design moves the whole app per realm instead. The switch was deleted rather
 * than left as dead configuration for someone to find and wonder about.
 *
 * ⚠ NOT for `/cdn-cgi/*`, a Cloudflare edge endpoint that exists only on the
 * proxied host, nor for page navigations like `/logout`, which are
 * server-rendered redirects that must stay on the host holding the cookie.
 */

import { getRealm, type Realm } from '@/lib/realm'

/** Build a URL for a backend path. Returns `path` unchanged — see above. */
export function apiUrl(path: string): string {
  return path
}

/**
 * Build an absolute WebSocket URL.
 *
 * Unlike `apiUrl` this cannot be a no-op: `new WebSocket()` rejects a relative
 * URL, so the current host is resolved explicitly.
 */
export function wsUrl(path: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}${path}`
}

// ── Hosts for clients without the session cookie (CLA-1980) ────────────────

/**
 * Hosts that serve the PRODUCTION SPA, where one page host fronts both realms.
 *
 * `claro.clinic` is routed to AU or US at the edge by the HttpOnly
 * `claro_realm` cookie. The SPA's own calls carry that cookie, which is why
 * `apiUrl` can stay a no-op. An MCP client, a `curl` or a partner's server
 * does not: sent to `claro.clinic` it lands on AU, whatever realm the key
 * belongs to. So a URL we hand to such a client must name its realm's host.
 *
 * Every other host (staging, demo, a dev server) is one deployment, and its
 * own origin is the right answer: a staging key only works on staging.
 */
const PRODUCTION_WEB_HOSTS = new Set([
  'claro.clinic',
  'api-au.claro.clinic',
  'api.us.claro.clinic',
  'api-us.claro.clinic',
])

/**
 * The app API (`/api/v1/…`) host per realm, as the mobile apps pin it
 * (claro-ios `RealmPinning.swift`: AU `api-au.claro.clinic`, US
 * `api.us.claro.clinic`). The backend's `ClaroWeb.PublicURL.api_url/0` makes
 * the same choice for iCal feed URLs.
 */
const APP_API_ORIGIN: Record<Realm, string> = {
  au: 'https://api-au.claro.clinic',
  us: 'https://api.us.claro.clinic',
}

/**
 * The public API (`/api/public/v1`) host per realm: claro-elixir
 * `Claro.PublicApi.base_url/1` (#1747), the host the spec `servers`,
 * `llms.txt` and the 421 `wrong_region` refusal name.
 */
const PUBLIC_API_ORIGIN: Record<Realm, string> = {
  au: 'https://api-au.claro.clinic',
  us: 'https://api-us.claro.clinic',
}

export interface HostContext {
  /** Defaults to the signed-in realm (`getRealm()`, from `/auth/me`). */
  realm?: Realm
  /** Defaults to the page's own location. */
  location?: Pick<Location, 'host' | 'origin'>
}

function originFor(table: Record<Realm, string>, ctx: HostContext): string {
  const loc = ctx.location ?? location
  if (!PRODUCTION_WEB_HOSTS.has(loc.host)) return loc.origin
  return table[ctx.realm ?? getRealm()]
}

/**
 * Absolute app-API URL for a client that holds no session cookie, such as the
 * MCP reporting endpoint in a `claude mcp add` snippet. Never for the SPA's
 * own calls: those go through `apiUrl` and the cookie.
 */
export function machineApiUrl(path: string, ctx: HostContext = {}): string {
  return `${originFor(APP_API_ORIGIN, ctx)}${path}`
}

/** Base URL of public API v1 for the signed-in realm, without a trailing slash. */
export function publicApiBaseUrl(ctx: HostContext = {}): string {
  return `${originFor(PUBLIC_API_ORIGIN, ctx)}/api/public/v1`
}
